<template>
  <v-data-iterator
    :items="sortedUserList"
    :items-per-page="20"
    :loading="fetchingUsers"
    style="width: 100%;"
    return-object
  >
    <template v-slot:loader>
      <v-row class="users-list-loading-spinner">
        <v-col class="text-center">
          <v-progress-circular
            indeterminate
            size="70"
            width="7"
            color="primary"
            :aria-label="$t('routes.users.loadingList')"
          />
          <div>{{ $t('routes.users.loadingList') }}</div>
        </v-col>
      </v-row>
    </template>

    <template v-slot:no-data>
      <v-row class="users-list-no-results">
        <v-col class="text-center">
          <div>{{$t('routes.users.noUsersFound')}}</div>
        </v-col>
      </v-row>
    </template>

    <template v-slot:header>
      <div class="d-flex user-list-view-row users-list-view-header-row">
        <div>&nbsp;</div>
        <div v-on:click="setSortBy('username')" data-cy="console-users-sortByUsername">
          {{ $t('routes.users.name') }}
          <span v-if="sortBy === 'username'" class="">
            <v-icon size="small" v-show="sortDesc">mdi-sort-ascending</v-icon>
            <v-icon size="small" v-show="!sortDesc">mdi-sort-descending</v-icon>
          </span>
        </div>
        <div v-on:click="setSortBy('email')" class="" data-cy="console-users-sortByEmail">
          {{ $t('routes.users.emailAddress') }}
          <span v-if="sortBy === 'email'">
            <v-icon size="small" v-show="sortDesc">mdi-sort-ascending</v-icon>
            <v-icon size="small" v-show="!sortDesc">mdi-sort-descending</v-icon>
          </span>
        </div>
        <div v-on:click="setSortBy('rootNodes')" class="" data-cy="console-users-sortByUserType">
          {{ $t('routes.users.rootFolders') }} ({{ $t('routes.users.type') }})
          <span v-if="sortBy === 'rootNodes'">
            <v-icon size="small" v-show="sortDesc">mdi-sort-ascending</v-icon>
            <v-icon size="small" v-show="!sortDesc">mdi-sort-descending</v-icon>
          </span>
        </div>
        <div v-on:click="setSortBy('createdAt')" class="" data-cy="console-users-sortByDate">
          {{ $t('routes.users.createdAt') }}
          <span v-if="sortBy === 'createdAt'">
            <v-icon size="small" v-show="sortDesc">mdi-sort-ascending</v-icon>
            <v-icon size="small" v-show="!sortDesc">mdi-sort-descending</v-icon>
          </span>
        </div>

        <div>

          <v-menu v-if="$canControlTenantConfig()" v-model="showingMenu">
            <template v-slot:activator="{ props }">
              <v-btn
                icon
                variant="text"
                width="32"
                height="32"
                rounded="circle"
                v-bind="props"
              >
                <v-icon>mdi-dots-vertical</v-icon>
              </v-btn>
            </template>

            <v-list>
              <v-list-item @click="openMFATenantDialog()">
                <template v-slot:prepend>
                  <v-icon size="small">mdi-two-factor-authentication</v-icon>
                </template>

                <v-list-item-title>
                  {{ isMFAEnabled ? $t('menus.configuration.disableMFA') : $t('menus.configuration.enableMFA')}}
                </v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>

          <MFATenantDialog
          v-model="showingMFATenantDialog"
          />
        </div>
      </div>
    </template>

    <template v-slot:default="{ items }">
      <div class="mt-2">
        <div v-for="item in items" :key="item.value.id">
          <UserListItem
            :item="item.value"
            :node="currentNode"
            :sort-by="sortBy"
            :sort-desc="sortDesc"
            class="list-view-item user-list-view-row align-center"
          >
          </UserListItem>
        </div>
      </div>
    </template>

    <template v-slot:footer="{ pagination, options }">
      <v-data-table-footer
        :pagination="pagination"
        :options="options"
        :items-per-page-options="footerOptions['items-per-page-options']"
        style="font-size: 10px;"
      />
    </template>

  </v-data-iterator>
</template>

<script>
import { useAuthStore } from '@/stores/AuthStore.js'
import { useMainStore } from '@/stores/MainStore.js'
import { useUserStore } from '@/stores/UserStore.js'
import { useNodeStore } from '@/stores/NodeStore.js';
import UserListItem from './UserListItem.vue'
import MFATenantDialog from './dialogs/MFATenantDialog.vue'

export default {
  name: "UsersListView",
  components: {
    UserListItem,
    MFATenantDialog,
  },
  props: {
    userList: {
      type: Array,
      required: true,
    },
    fetchingUsers: {
      type: Boolean,
      required: true,
    },
  },
  data: function() {
    return {
      sortBy: '',
      sortDesc: false,
      showingMenu: false,

      showingMFATenantDialog: false,
    };
  },
  beforeMount: function() {
    const userStore = useUserStore();
    userStore.resetSelectedUsers();
  },
  mounted: function() {
    const userStore = useUserStore();
    const sortedBy = userStore.getSortedBy;

    this.sortBy = sortedBy.column;
    this.sortDesc = sortedBy.desc;
  },
  methods: {
    openMFATenantDialog: function() {
      this.showingMenu = false;
      this.showingMFATenantDialog = true;
    },
    showMenu: function(componentName) {
      const mainStore = useMainStore();
      mainStore.setActiveMenu(componentName);
    },
    setSortBy: function(sortByColumn) {
      if (this.sortBy === sortByColumn) {
        this.sortDesc = !this.sortDesc;
        if (!this.sortDesc) {
          this.sortDesc = false;
        }
      } else {
        this.sortDesc = false;
        this.sortBy = sortByColumn;
      }
    },
    sortByUsername: function (a, b) {
      return a.name.localeCompare(b.name);
    },
    sortByEmail: function(a, b) {
      return a.email.localeCompare(b.email)
    },
    sortByRootNodes: function(a, b) {
      let rootNodesA = this.getRootNodes(a);
      let rootNodesB = this.getRootNodes(b);

      return rootNodesA.localeCompare(rootNodesB);
    },
    sortByCreatedAt: function(a, b) {
      const dateA = new Date(a.createdat);
      const dateB = new Date(b.createdat);

      return dateA - dateB;
    },
    sortByUsertype: function(a, b) {
      const userTypeA = this.determineUserType(a);
      const userTypeB = this.determineUserType(b);

      // 1. Handle Null Cases First
      if (userTypeA === null && userTypeB === null) {
        return 0; // Both null, consider them equal
      } else if (userTypeA === null) {
        return 1; // A is null, so B comes before A
      } else if (userTypeB === null) {
        return -1; // B is null, so A comes before B
      }

      // 2. Compare User Types (if neither is null)
      return userTypeA.localeCompare(userTypeB);
    },
    getRootNodes: function(user) {
      let nodeNames = [];

      if (Array.isArray(user.tenants) && user.tenants.length > 0) {
        for (const tenant of user.tenants) {
          const accessPoints = tenant?.accessPoints;

          if (Array.isArray(accessPoints) && accessPoints.length > 0) {
            for (const accessPoint of accessPoints) {
              if (accessPoint?.node?.name) {
                nodeNames.push(accessPoint.node.name);
              }
            }
          }
        }
      }

      // Sort the node names
      nodeNames.sort((a, b) => a.localeCompare(b));

      return nodeNames.join(", ");
    },
    determineUserType: function(user) {
      let userType = null;
      if (user?.tenants && Array.isArray(user.tenants) && user.tenants.length > 0) {
        userType = user.tenants.find((tenant) => tenant.id === this.activeTenantId)?.userType || 'client';

        for (const tenant of user.tenants) {
          if (tenant.userType === 'teamMember') {
            userType = 'teamMember';
            break;
          }
        }
      }

      return userType || null;
    },
  },
  computed: {
    sortedUserList: function() {
      // remove the current user from the list
      let items = this.userList.filter((x) => x.id !== this.currentUserId);

      switch (this.sortBy) {
        case 'username': items.sort(this.sortByUsername); break;
        case 'email': items.sort(this.sortByEmail); break;
        case 'rootNodes': items.sort(this.sortByRootNodes); break;
        case 'createdAt': items.sort(this.sortByCreatedAt); break;
        default: break;
      }

      if (this.sortDesc) {
        return items.reverse();
      }

      return items;
    },
    userTypes: function() {
      const userStore = useUserStore();
      const userTypes = userStore.getUsertypes;

      const userTypeMap = new Map();
      for (let i = 0; i < userTypes.length; i++) {
        userTypeMap.set(userTypes[i].id, userTypes[i]);
      }

      return userTypeMap;
    },
    currentNode: function() {
      const nodeStore = useNodeStore();
      return nodeStore.getCurrentNode;
    },
    footerOptions: function() {
      return {
        'items-per-page-options': [20, 30, 50, 100],
        'pagination-text': this.$t('common.itemsPerPage')
      };
    },
    currentUserId: function() {
      const authStore = useAuthStore();
      const dbUser = authStore.dbUser;

      return dbUser?.id || null;
    },
    activeTenantId: function() {
      const authStore = useAuthStore();
      return authStore.getActiveTenantId;
    },
    isMFAEnabled: function() {
      const authStore = useAuthStore();
      const tenant = authStore.getActiveTenant;

      return tenant  ? tenant.mfarequired : false;
    },
  }
}
</script>

<style>

.users-list-view-header-row {
  font-weight: 600;
  cursor: pointer;
  height: 54px;
  color: #000000;
  background-color: #3dabff;
  border-radius: 10px;
  align-items: center;
}

.users-list-view-header-row i.v-icon {
  color: #000000;
}

.user-list-view-row { padding-left: 8px; padding-right: 8px;}
.user-list-view-row > div {
  display: flex;
  align-items: center;
  /* padding: 0 16px; */
  word-break: normal;
}

.user-list-view-row > div:first-child {
  text-align: left;
}
.user-list-view-row > div:last-child {
  text-align: right;
}

/* Normal screens */
.user-list-view-row > div:nth-child(1) {width: 5%;}     /* select box */
.user-list-view-row > div:nth-child(2) {width: 20%;}    /* names */
.user-list-view-row > div:nth-child(3) {width: 20%; word-wrap: break-word; display: inline-block;}    /* email */
.user-list-view-row > div:nth-child(4) {width: 35%; padding-right: 10%; }    /* user type @ root nodes */
.user-list-view-row > div:nth-child(5) {width: 15%;}    /* created at */
.user-list-view-row > div:nth-child(6) {width: 5%; justify-content: end;}     /* actions */

/* Small screens */
@media only screen and (max-width: 960px) {
  .user-list-view-row > div:nth-child(1) {width: 10%;}     /* select box */
  .user-list-view-row > div:nth-child(2) {width: 50%;}    /* names */
  .user-list-view-row > div:nth-child(3) {display: none;}    /* email */
  .user-list-view-row > div:nth-child(4) {display: none;}    /* user type @ root nodes */
  .user-list-view-row > div:nth-child(5) {width: 30%;}     /* created at */
  .user-list-view-row > div:nth-child(6) {width: 10%;}     /* actions */
}

.users-list-loading-spinner {
  padding-top: 24px;
}
</style>
